import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useHistory, Link } from "react-router-dom";
import { AuthContext } from "../../helpers/AuthContext";
import { useNotifications } from "../../components/NotificationContext";
import toast from "react-hot-toast";
import { Visibility, VisibilityOff, EmailOutlined, LockOutlined, ArrowForward, ShieldOutlined } from "@mui/icons-material";
import "./EliteLogin.css";

const ACTIVATION_LOGO = `${process.env.PUBLIC_URL || ""}/logo.png`;
const ACTIVATION_DURATION_MS = 2800;
const BOLT_POINTS = "0,175 170,155 240,180 342,65 409,120 505,91 575,25 640,115 751,72 810,145 915,65 1000,109 1090,55 1250,75";

function Activation({ firstName, lastName, photo, onComplete, reducedMotion }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(onComplete, reducedMotion ? 150 : ACTIVATION_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [onComplete, reducedMotion]);
  // getHours() uses the browser's local time zone.
  const localHour = new Date().getHours();
  const greeting = localHour >= 5 && localHour < 12
    ? "Good morning"
    : localHour >= 12 && localHour < 17
      ? "Good afternoon"
      : "Good evening";
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  const initials = name ? (firstName?.[0] || "").concat(lastName?.[0] || firstName?.[1] || "").toUpperCase() : "EC";
  return (
    <section className="ec-activation" role="status" aria-label="Signing in to Elite Core">
      <div className="ec-activation-grid" />
      <div className="ec-emblem">
        <div className="ec-bolt" aria-hidden="true">
          <svg viewBox="0 0 1250 260" preserveAspectRatio="none">
            <polyline className="glow" points={BOLT_POINTS} />
            <polyline className="main" points={BOLT_POINTS} />
            <polyline className="white" points={BOLT_POINTS} />
          </svg>
        </div>
        <div className="ec-target" aria-hidden="true" />
        <div className="ec-spark" aria-hidden="true" />
        <img className="ec-core-logo" src={ACTIVATION_LOGO} alt="Elite Core" />
      </div>
      <div className="ec-personal">
        <div className="ec-avatar-ring"><div className="ec-avatar">
          {photo && !imageFailed ? <img src={photo} alt="" onError={() => setImageFailed(true)} /> : initials}
        </div></div>
        <p>{greeting}{firstName ? <>, <strong>{firstName}</strong></> : ""}</p>
      </div>
    </section>
  );
}

function useLoginSound() {
  const sound = useRef(null);
  useEffect(() => {
    const audio = new Audio(`${process.env.PUBLIC_URL || ""}/sound-effects/log-in-successful.mp3`);
    audio.preload = "auto";
    audio.volume = 0.7;
    sound.current = audio;
    return () => { sound.current = null; };
  }, []);
  const prime = () => {
    const audio = sound.current;
    if (!audio) return;
    audio.muted = true;
    audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => {}).finally(() => { audio.muted = false; });
  };
  const play = () => {
    const audio = sound.current;
    if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
  };
  return { prime, play };
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phase, setPhase] = useState("form");
  const [leavingForReset, setLeavingForReset] = useState(false);
  const [profile, setProfile] = useState({});
  const { authState, setAuthState } = useContext(AuthContext);
  const { fetchSocket } = useNotifications();
  const history = useHistory();
  const { prime, play } = useLoginSound();
  const pending = useRef(false);
  const timers = useRef([]);
  const navigated = useRef(false);
  const reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (localStorage.getItem("accessToken")) history.push("/");
    return () => { timers.current.forEach(window.clearTimeout); };
  }, [history]);

  const navigate = useCallback(() => {
    if (navigated.current) return;
    navigated.current = true;
    history.push("/", authState);
  }, [history, authState]);

  const openForgotPassword = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (leavingForReset || phase !== "form") return;
    setLeavingForReset(true);
    timers.current.push(window.setTimeout(() => history.push("/forgot-password"), 420));
  };

  const login = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/login`, { email, password });
      if (response.data.error) {
        toast.error(response.data.error, { id: 1 });
        pending.current = false;
        setBusy(false);
        return;
      }
      const data = response.data;
      localStorage.setItem("accessToken", data.token);
      setAuthState({
        email: data.email, id: data.id, first_name: data.first_name,
        role: data.role, instanceId: data.instanceID, status: true
      });
      setProfile({
        firstName: data.first_name || data.firstName || "",
        lastName: data.last_name || data.lastName || "",
        photo: data.profile_picture || data.profilePicture || data.profile_image || data.avatar || ""
      });
      // Match App.js profile avatar retrieval without delaying a successful sign-in.
      // The profile field can be JSON, an array of uploaded images, or a URL.
      axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/basicinfo/${data.id}`)
        .then(({ data: info }) => {
          let image = info?.profilePicture;
          if (typeof image === "string") {
            try { image = JSON.parse(image); } catch (_) { /* Already a URL */ }
          }
          if (Array.isArray(image)) image = image.find(entry => entry?.selected) || image[0];
          if (image && typeof image === "object") image = image.url || image.src || image.path;
          if (typeof image === "string" && image.trim()) {
            const photo = image.startsWith("uploads")
              ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${image}`
              : image;
            setProfile(current => ({ ...current, photo }));
          }
        })
        .catch(() => { /* Keep avatar from login response or initials fallback */ });
      fetchSocket();
      play();
      setPhase("fading");
      timers.current.push(window.setTimeout(() => setPhase("activation"), reducedMotion ? 0 : 560));
      // Safety fallback: never strand an authenticated user if the animation cannot finish.
      timers.current.push(window.setTimeout(navigate, reducedMotion ? 750 : 5200));
    } catch (error) {
      toast.error("There was an error establishing a connection to the server. Please verify you are connected to the internet.", { id: 2 });
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="ec-login">
      <div className={`ec-login-screen${phase !== "form" ? " exiting" : leavingForReset ? " ec-auth-page-exit" : ""}`} aria-hidden={phase !== "form"}>
        <div className="ec-login-page">
          <section className="ec-login-card" aria-label="Elite Core login">
            <div className="ec-login-formside">
              <h1 className="ec-login-title">Login</h1>
              <div className="ec-login-subtitle">Elite Core Portal</div>
              <p className="ec-login-intro">Your projects, orders, and daily work - <strong>all in one place.</strong></p>
              <form className="ec-login-form" onSubmit={login}>
                <label className="ec-login-field">
                  <span className="ec-login-fieldicon"><EmailOutlined /></span>
                  <input type="email" aria-label="Email" autoComplete="username" placeholder="Type your email" value={email} onChange={e => setEmail(e.target.value)} required disabled={busy} />
                </label>
                <label className="ec-login-field">
                  <span className="ec-login-fieldicon"><LockOutlined /></span>
                  <input type={showPassword ? "text" : "password"} aria-label="Password" autoComplete="current-password" placeholder="Type your password" value={password} onChange={e => setPassword(e.target.value)} required disabled={busy} />
                  <button className="ec-login-eye" type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(v => !v)}>{showPassword ? <VisibilityOff /> : <Visibility />}</button>
                </label>
                <button className="ec-login-submit" type="submit" disabled={busy} onMouseDown={prime} onTouchStart={prime}>
                  {busy ? "SIGNING IN..." : "LOGIN"} <ArrowForward />
                </button>
              </form>
              <Link to="/forgot-password" onClick={openForgotPassword} className="ec-login-forgot"><LockOutlined fontSize="small" /> Forgot password?</Link>
              <div className="ec-login-secure">
                <span className="ec-login-secureicon"><ShieldOutlined /></span>
                <div className="ec-login-securebody"><strong>Secure sign-in</strong><small>Your account and work are protected.</small></div>
              </div>
            </div>
            <aside className="ec-login-photo" aria-label="Elite showroom" style={{ backgroundImage: "linear-gradient(0deg, rgba(7,10,15,.82), transparent 65%), url(\'register-login-pages/elite-core-login.jpg\')" }}>
              
              <div className="ec-login-phototext"><h2>Good work starts here.</h2><p>From the first idea to the finished order, keep things moving.</p></div>
            </aside>
          </section>
        </div>
      </div>
      {phase === "activation" && <Activation {...profile} reducedMotion={reducedMotion} onComplete={navigate} />}
    </div>
  );
}

export default Login;
